import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "../../../../components/card";
import { Button } from "../../../../components/button";
import { ErrorPlaceholder } from "../../../../components/common";
import FormHeader from "../../../../components/form/FormHeader";
import { Badge } from "../../../../components/badge";
import { BarChart3, PieChart, BarChart2 } from "lucide-react";
import { QuestionResponseChart } from "../components/response-analytics";
import { useGetSurveySummary } from "../hooks";
import type { SurveySummaryQuestion } from "../types/survey.types";
import totalResponsesIcon from "../assets/icons/total-responses.svg";
import totalQuestionsIcon from "../assets/icons/total-questions.svg";
import responseRateIcon from "../assets/icons/response-rate.svg";
import { SurveyResponsesSkeleton } from "../components/survey-skeletons";
import { goBackOrGoHome } from "../../../../utils/navigation";

type SurveyResponsesPageProps = {
  basePath?: string;
};

function SurveyResponsesPage({ basePath = "/survey" }: SurveyResponsesPageProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { summaryData, setSummaryData, loading, error, refresh: refetchSummary } =
    useGetSurveySummary(id);

  // Use the helper function for formatting date labels

  const [isDarkMode, setIsDarkMode] = useState(false);

  // Check for dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains("dark"));
    };

    checkDarkMode();

    // Watch for dark mode changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!summaryData?.questions?.length) {
      return;
    }

    const filteredQuestions = summaryData.questions
      .filter((q: SurveySummaryQuestion) => q?.type && q.type !== "location")
      .map((q: SurveySummaryQuestion, index: number) => ({
        ...q,
        position: index + 1,
      }));

    const hasChanged =
      filteredQuestions.length !== summaryData.questions.length ||
      filteredQuestions.some(
        (question, index) => question.position !== summaryData.questions[index]?.position,
      );

    if (hasChanged) {
      setSummaryData({
        ...summaryData,
        questions: filteredQuestions,
      });
    }
  }, [summaryData, setSummaryData]);

  const getQuestionIcon = (type: string) => {
    switch (type) {
      case "multiple_choice":
        return <PieChart className="h-4 w-4 text-gray-600 dark:text-gray-300" />;
      case "rating":
      case "scale":
        return <BarChart2 className="h-4 w-4 text-gray-600 dark:text-gray-300" />;
      case "yes_no":
        return <BarChart3 className="h-4 w-4 text-gray-600 dark:text-gray-300" />;
      default:
        return <BarChart3 className="h-4 w-4 text-gray-600 dark:text-gray-300" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <SurveyResponsesSkeleton />
      </div>
    );
  }

  if (error || !summaryData) {
    return (
      <div className="p-6">
        <Card className="p-6 dark:bg-gray-800 dark:border-gray-600">
          <ErrorPlaceholder
            error={error || "Survey responses not found"}
            onRetry={() => void refetchSummary()}
          />
          <div className="flex justify-center">
            <Button onClick={() => goBackOrGoHome(navigate, undefined, basePath)}>
              Back to Survey
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <FormHeader
        onBack={() => goBackOrGoHome(navigate, undefined, basePath)}
        title={summaryData.title}
        subtitle="Survey Responses"
        titleIcon={<BarChart3 className="h-5 w-5 text-[#00C49A]" />}
      />
      <Card className="p-6 dark:bg-gray-800 dark:border-gray-600">
        {/* Statics */}
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Statics</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="relative p-6 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-600 shadow-sm">
            <div className="absolute top-4 right-4 w-8 h-8 rounded-lg flex items-center justify-center bg-[#F2FCFA] dark:bg-emerald-900/30">
              <img
                src={totalResponsesIcon}
                alt=""
                aria-hidden="true"
                width="18"
                height="18"
              />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                Total Responses
              </p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {summaryData.total_responses}
              </p>
            </div>
          </div>
          <div className="relative p-6 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-600 shadow-sm">
            <div className="absolute top-4 right-4 w-8 h-8 rounded-lg flex items-center justify-center bg-[#F2FCFA] dark:bg-emerald-900/30">
              <img
                src={totalQuestionsIcon}
                alt=""
                aria-hidden="true"
                width="17"
                height="18"
              />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                Total Questions
              </p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {summaryData.questions.length}
              </p>
            </div>
          </div>
          <div className="relative p-6 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-600 shadow-sm">
            <div className="absolute top-4 right-4 w-8 h-8 rounded-lg flex items-center justify-center bg-[#F2FCFA] dark:bg-emerald-900/30">
              <img
                src={responseRateIcon}
                alt=""
                aria-hidden="true"
                width="17"
                height="18"
              />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                Avg. Response Rate
              </p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {summaryData.questions.length > 0
                  ? Math.round(
                      (summaryData.questions.reduce((sum, q) => sum + q.answered_count, 0) /
                        (summaryData.questions.length * summaryData.total_responses)) *
                        100,
                    )
                  : 0}
                %
              </p>
            </div>
          </div>
        </div>

        {/* Question Statics */}
        <div className="space-y-8">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Question Statics
            </h2>
          </div>

          {summaryData.questions.map((question, index) => {
            // Question types that have dedicated response components
            const hasDedicatedComponent = [
              "multiple_choice",
              "multiple_answer",
              "short_answer",
              "long_answer",
              "date",
              "time",
              "datetime",
              "rating",
              "scale",
              "yes_no",
              "dropdown",
              "email",
              "phone",
              "number",
              "file_upload",
              "multiple_choice_grid",
              "checkbox_grid",
            ].includes(question.type);

            return (
              <div key={question.question_id}>
                {hasDedicatedComponent ? (
                  <QuestionResponseChart question={question} totalResponses={summaryData?.total_responses || 0} isDarkMode={isDarkMode} />
                ) : (
                  <Card className="p-6 dark:bg-gray-800 dark:border-gray-600">
                    <div className="mb-6">
                      <div className="flex items-start gap-3 mb-2">
                        {getQuestionIcon(question.type)}
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                            {question.position}. {question.title}
                          </h3>
                          <div className="flex items-center gap-4 mt-2">
                            <Badge
                              variant="outline"
                              className="text-xs capitalize dark:border-gray-600 dark:text-gray-200"
                            >
                              {question.type.replace("_", " ")}
                            </Badge>
                            <span className="text-sm text-gray-600 dark:text-gray-200">
                              {question.answered_count} of {summaryData.total_responses} responses
                              {question.unanswered_count !== undefined && (
                                <span className="text-gray-400 dark:text-gray-400 ml-1">
                                  ({question.unanswered_count} unanswered)
                                </span>
                              )}
                            </span>
                            <span className="text-sm font-medium text-[#004232] dark:text-[#00D6A8]">
                              {(
                                (question.answered_count / summaryData.total_responses) *
                                100
                              ).toFixed(1)}
                              % response rate
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6">
                      {question.options.length > 0 ? (
                        <QuestionResponseChart question={question} totalResponses={summaryData?.total_responses || 0} isDarkMode={isDarkMode} />
                      ) : (
                        <div className="flex items-center justify-center h-32 text-gray-600 dark:text-gray-300 font-medium">
                          No responses available for this question
                        </div>
                      )}
                    </div>
                  </Card>
                )}
              </div>
            );
          })}
        </div>

        {summaryData.questions.length === 0 && (
          <div className="text-center py-8 text-gray-600 dark:text-gray-300 font-medium">
            No questions found for this survey.
          </div>
        )}
      </Card>
    </div>
  );
}

export default SurveyResponsesPage;
